import classnames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import styles from './styles.module.scss';
import type { PopoverProps } from '../Popover/index.d.ts';
import PopOver, { PLACEMENTS } from '../Popover/index.js';
import type { InferPropTypes } from '../typings/index.ts';
import type { Variant } from '../utils/addVariants/index.ts';
import addVariants from '../utils/addVariants/index.ts';

export const COLORS = ['light', 'blue', 'green', 'orange', 'red'];

export const VARIANTS = ['default', 'wrap', 'dark', 'no-padding', 'no-arrow'].concat(COLORS);

export type Placement = (typeof PLACEMENTS)[number];

const propTypes = {
  content: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
  /**
   * The direction to place the tool tip
   * @default 'bottom'
   */
  placement: PropTypes.oneOf<Placement>(PLACEMENTS),
  /**
   * Whether auto placement (repositioning) is disabled
   */
  placementRepositioningEnabled: PropTypes.bool,
  /**
   * The activator for the tooltip
   */
  children: PropTypes.oneOfType([PropTypes.node.isRequired, PropTypes.func.isRequired]).isRequired,
  /**
   * Decides the variant of the button
   */
  variant: PropTypes.oneOfType([
    PropTypes.oneOf(VARIANTS),
    PropTypes.bool,
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.oneOf(VARIANTS), PropTypes.bool]))
  ]),
  /**
   * Additional classnames to apply
   */
  className: PropTypes.string,
  /**
   * Additional classnames to apply to the container
   */
  containerClassName: PropTypes.string,
  /**
   * Whether to allow opening the tooltip, overrides all state
   * @default false
   */
  isDisabled: PropTypes.bool,
  /**
   * Forces the tooltip to be open
   */
  forceOpen: PropTypes.bool,
  /**
   * Disables the tab index
   * @default false
   */
  tabIndexDisabled: PropTypes.bool
};

const defaultProps = {
  className: '',
  containerClassName: '',
  placement: 'bottom',
  placementRepositioningEnabled: true,
  variant: 'dark',
  isDisabled: false,
  forceOpen: false,
  tabIndexDisabled: false
};

export type TooltipProps = InferPropTypes<typeof propTypes, typeof defaultProps>;

const Tooltip: React.FC<TooltipProps> = ({
  content,
  placement,
  placementRepositioningEnabled,
  children: activator,
  className,
  containerClassName,
  variant,
  isDisabled,
  forceOpen,
  tabIndexDisabled,
  ...props
}: TooltipProps) => {
  const variants = ([] as Variant[]).concat(variant);
  // If no color specified default to dark
  if (!variants.some(v => COLORS.includes(v as string))) {
    variants.push('dark');
  }
  // If no padding amount specified default to dense
  if (!variants.includes('no-padding')) {
    variants.push('dense');
  }
  // If no-arrow is not defined default to having an arrow
  if (!variants.includes('no-arrow')) {
    variants.push('arrow');
  }
  return (
    <PopOver
      variant={variants.filter(v => v !== 'light' && v !== 'no-arrow') as PopoverProps['variant']}
      placement={placement as Placement}
      placementRepositioningEnabled={placementRepositioningEnabled}
      activator={activator}
      className={classnames(addVariants('aglmd-tooltip', styles, variant), className)}
      containerClassName={classnames(
        addVariants(
          'aglmd-tooltip__container',
          styles,
          isDisabled ? 'disabled' : 'enabled',
          forceOpen ? 'active' : false
        ),
        containerClassName
      )}
      isOpen={!isDisabled}
      {...{ tabIndex: tabIndexDisabled ? undefined : 0, ...props }}
    >
      {content}
    </PopOver>
  );
};

Tooltip.propTypes = propTypes;

Tooltip.defaultProps = defaultProps;

export default Tooltip;
